import { auth, db, deleteUserAccountCloudFunction } from '@/firebase/init';
import { EntityType } from '@/types/Organization';
import { InvitationFromStore, Permission, UserStateData } from '@/types/types';
import {
  collectionGroup,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  where,
  FieldValue,
  getDoc,
} from 'firebase/firestore';

type Workspaces = string[];
type Organizations = string[];

export class UserService {
  static async deleteUser() {
    await deleteUserAccountCloudFunction();
    await auth.signOut();
  }

  static async get(userId: string) {
    const userSnap = await getDoc(doc(db, `users/${userId}`));
    if (!userSnap.exists()) throw new Error('User does not exist');
    return userSnap.data() as UserStateData;
  }

  static async getEntitiesIds(userId: string) {
    const userSnap = await getDoc(doc(db, `users/${userId}`));
    if (!userSnap.exists()) throw new Error('User does not exist');
    const workspaces: Workspaces = userSnap.data()?.workspaces || [];
    const organizations: Organizations = userSnap.data()?.organizations || [];
    return [organizations, workspaces] as const;
  }

  static async getWorkspacesInvitations(userId: string) {
    const invitations: InvitationFromStore<'workspace'>[] = [];
    const snapshot = await getDocs(
      query(
        collectionGroup(db, 'invitations'),
        where('invitedUserId', '==', userId),
        orderBy('createdAt', 'desc'),
      ),
    );
    snapshot.forEach((invitation) => {
      invitations.push({
        ...invitation.data(),
        id: invitation.id,
      } as InvitationFromStore<'workspace'>);
    });
    return invitations;
  }

  static async getOrganizationsInvitations(userId: string) {
    const invitations: InvitationFromStore<'organization'>[] = [];
    const snapshot = await getDocs(
      query(
        collectionGroup(db, 'organization_invitations'),
        where('invitedUserId', '==', userId),
        orderBy('createdAt', 'desc'),
      ),
    );
    snapshot.forEach((invitation) => {
      invitations.push({
        ...invitation.data(),
        id: invitation.id,
      } as InvitationFromStore<'organization'>);
    });
    return invitations;
  }

  static async acceptInvitation(
    entityId: string,
    invitationId: string,
    userId: string,
    entityType: EntityType,
  ) {
    return await runTransaction(db, async (t) => {
      const invitationsCollectionName =
        entityType === 'workspaces' ? 'invitations' : 'organization_invitations';
      const invitationRef = doc(
        db,
        `${entityType}/${entityId}/${invitationsCollectionName}`,
        invitationId,
      );
      const invitation = await t.get(invitationRef);
      if (!invitation?.exists()) throw new Error('Invitation does not exist');
      type ConditionalInvitation<T extends string> = T extends `${infer U}s` ? U : never;
      const invitationData = invitation.data() as InvitationFromStore<
        ConditionalInvitation<typeof entityType>
      >;
      const permissionData: Omit<Permission, 'createdAt'> & { createdAt: FieldValue } = {
        role: invitation.data().role,
        addedBy: invitationData.invitingUserEmail,
        email: invitationData.invitedUserEmail,
        createdAt: serverTimestamp(),
        name: invitationData.name,
        organizationId: invitationData.organizationId,
      };

      if (entityType === 'workspaces') {
        permissionData.workspaceId = (
          invitationData as InvitationFromStore<'workspace'>
        ).workspaceId;
      }

      t.set(
        doc(
          db,
          `${entityType}/${entityId}/${
            entityType === 'workspaces' ? 'permissions' : 'organization_permissions'
          }`,
          userId,
        ),
        permissionData,
      );
      t.delete(invitationRef);
    });
  }

  static async deleteInvitation(
    entityId: string,
    invitationId: string,
    entityType: EntityType,
  ) {
    const invitationsCollectionName =
      entityType === 'workspaces' ? 'invitations' : 'organization_invitations';
    return await deleteDoc(
      doc(db, `${entityType}/${entityId}/${invitationsCollectionName}`, invitationId),
    );
  }

  static async deleteInvitationByHash(
    hash: string,
    invitationType: string,
    userId: string,
  ) {
    const snapshot = await getDocs(
      query(
        collectionGroup(
          db,
          invitationType === 'workspaces' ? 'invitations' : 'organization_invitations',
        ),
        where('hash', '==', hash),
        where('invitedUserId', '==', userId),
      ),
    );
    snapshot.forEach((invitation) => {
      deleteDoc(invitation.ref);
    });
  }

  static async deleteStreamlitSession() {
    const userToken = auth.currentUser?.uid;
    if (!userToken) return;

    const snapshot = await getDocs(
      query(
        collectionGroup(db, 'streamlitSessions'),
        where('userToken', '==', userToken),
      ),
    );

    Promise.all(snapshot.docs.map((session) => deleteDoc(session.ref)));
  }
}
